/*
 * globalReducer
 * Description
 * The reducer takes care of our data. Using actions, we can
 * update our application state. To add a new action,
 * add it to the switch statement in the reducer function
 *
 * @version 1.0.0
 * @author Pietro Bertuccelli
 * @since 10-Oct-2024
 */

import { produce } from 'immer'
import { actionTypes } from '../actions/global'
import { nullSafe } from 'Utility/globalMethods'
import { Capacitor } from '@capacitor/core'

const ACTION_HANDLERS = {
  // @generator reducer:type:action
  [actionTypes.PHONE_PREFIXES_SUCCESS]: produce((draft, action) => {
    draft.phonePrefixes = nullSafe(() => action.response.data.internationalPrefixes, [])

    draft.defaultPrefix = nullSafe(() => action.response.data.defaultInternationalPrefix, initialState.defaultPrefix)
  }),
  [actionTypes.GET_MIN_VERSION_SUCCESS]: produce((draft, action) => {
    draft.minAppVersion = nullSafe(() => Capacitor.getPlatform() === 'ios' ? action.response.data.appVersions.IOS : action.response.data.appVersions.Android, '')
  }),
  [actionTypes.GET_AML_SURVEY_INFO_SUCCESS]: produce((draft, action) => {
    draft.amlSurveyInfo = nullSafe(() => action.response, {})
  }),
  [actionTypes.DO_SAVE_ACTION]: produce((draft, action) => {
    draft.savedAction = action.payload
  }),
  [actionTypes.DO_SET_SELECTED_TAB]: produce((draft, action) => {
    draft.selectedTab = action.payload
  }),
  [actionTypes.DO_SET_TRANSACTION_FILTER_SEARCH_NOTE_LIST]: produce((draft, action) => {
    const note = nullSafe(() => action.payload.searchedNote, '')
    if (typeof note === 'string' && note.trim().length > 0) {
      // Avoid duplicates (case insensitive)
      const exists = draft.localSearchList.some(n => n.toLowerCase() === note.trim().toLowerCase())
      if (!exists) {
        draft.localSearchList.push(note.trim())
      }
    }
  }),
  [actionTypes.DO_SET_HOME_LAST_TRANSACTION_DETAILS]: produce((draft, action) => {
    draft.homeLastTransactionDetails = action.payload
  }),
  [actionTypes.DO_SET_PROMO_END_DATE]: produce((draft, action) => {
    draft.promoEndDate = action.payload
  }),
  [actionTypes.DO_SET_FREE_MONTHS]: produce((draft, action) => {
    draft.freeMonths = action.payload
  }),
  [actionTypes.DO_SET_PAGOPA_BILLING_CONFIRMED]: produce((draft, action) => {
    draft.isPagoPaBillingDataConfirmed = action.payload
  }),
  [actionTypes.DO_SET_PCI_PROTECTION_LEVEL]: produce((draft, action) => {
    // Protection level management
    const surveySummary = nullSafe(() => action.payload, {})

    // computedProtectionLevel represents the protection level calculated in real time based on the answers given in the survey
    const computedProtectionLevel = nullSafe(() => surveySummary.protectionLevel, 'LOW')
    // surveyStatusCode represents the current status of the survey, 8 means 'remediation completed'
    const surveyStatusCode = nullSafe(() => surveySummary.statusCode, '')

    // Set the showed protection level only if the survey is not in 'remediation completed' status (8) or if it is empty
    if (!draft.pciProtectionLevel || surveyStatusCode !== 8) {
      draft.pciProtectionLevel = computedProtectionLevel
    }
  })
}

// The initial state of the global
const initialState = {
  phonePrefixes: [],
  defaultPrefix: {},
  minAppVersion: '',
  savedAction: null,
  selectedTab: null,
  localSearchList: [],
  homeLastTransactionDetails: null,
  promoEndDate: '',
  freeMonths: '',
  isPagoPaBillingDataConfirmed: false,
  pciProtectionLevel: null,
  amlSurveyInfo: {}
}

const global = (state = initialState, action) => {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

export default global
